import React, { useEffect, useRef, useState } from "react";
import english from "../../../assets/restaurant/images/english.png";
import japanese from "../../../assets/restaurant/images/japanese.png";
import Skeleton from "react-loading-skeleton";
import { LOADER_TIMEOUT_TIME, ORDER_PLATFORM, ORDER_STATUS } from "../../../utils/Constants";
import { useDispatch, useSelector } from "react-redux";
import { tableDetail } from "../services/table.service";
import { getLocalKey, getPersistentSessionKey, setLocalKey, TrimText, QRHeaderStatus, formatDateToHHMM, DT } from "../../../utils/commonfunction";
import { Link, useLocation, useNavigate } from "react-router-dom";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { addTableDetails } from "../../../redux/slices/qrcodeDetails";
import Swal from "sweetalert2";
import {
  QR_LANGUAGE_STORAGE_KEY,
  resolveQrLocale,
} from "../utils/qrLocale";
import { postAppMessage } from '../utils/nativeBridge';

function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // Re-pull tableDetail (and the cart badge count) whenever the order is
  // refreshed elsewhere, e.g. the /place Refresh button.
  const orderRefreshNonce = useSelector((state) => state.QrOrderToken.orderRefreshNonce);
  const [tableDetailsLoader, setTableDetailsLoader] = useState(true);
  const [tableData, setTableData] = useState({});
  const [token, setToken] = useState(getPersistentSessionKey("qr_user_token"));
  const [langChanged, setLangChanged] = useState(false);
  // Gates the one-time side effects (loader, reservation popup, redirect) inside
  // the tableDetail effect so only data refreshes when navigating between pages.
  const hasInitializedRef = useRef(false);
  // Total confirmed items on the table's active order (base + sub-orders),
  // computed backend-side and returned on activeOrder.item_count.
  const confirmedItemCount = Number(tableData?.activeOrder?.item_count) || 0;

  const queryParams = new URLSearchParams(window.location.search);
  const table_id = queryParams.get("table_id");
  const { t } = useTranslation();
  const lang = queryParams.get("language");
  const base = queryParams.get("base");
  const [showWebHeder, setShowWebHeder] = useState(false);

  const newToken = useSelector((state) => {
    return state.QrOrderToken.qrPanelToken;
  });
  useEffect(() => {
    setToken(newToken);
  }, [newToken]);

  const [flag, setFlag] = useState(() =>
    resolveQrLocale(getLocalKey(QR_LANGUAGE_STORAGE_KEY), lang) === "ja" ? japanese : english
  );

  useEffect(() => {
    if (token && token.length) {
      // Data (incl. activeOrder.item_count for the cart badge) is refreshed on
      // every page change so the count stays in sync after items are added. The
      // one-time side effects — loader, reservation popup, redirect to /place —
      // run only on the first load, gated by hasInitializedRef, so navigating
      // does not re-fire them.
      const isFirstLoad = !hasInitializedRef.current;
      if (isFirstLoad) {
        // Claim the first-load slot synchronously (before the async fetch) so a
        // second effect run that fires while this request is in flight is gated
        // out — otherwise both runs capture isFirstLoad=true and fire the
        // one-time side effects (reservation popup, redirect to /place) twice.
        hasInitializedRef.current = true;
        setTableDetailsLoader(true);
      }
      tableDetail({ o_id: table_id }, { token: token })
        .then((response) => {
          setTableData(response.data);
          dispatch(addTableDetails(response.data));

          if (!isFirstLoad) {
            return;
          }

          if(response?.data?.upcoming_reservations && window.location.pathname==='/qr-order'){
            const reservationData =response?.data?.upcoming_reservations_data;
            const reservationTime = formatDateToHHMM(new Date(reservationData.start_time));
            let qr_popup_text = DT(t('qr_popup_text'),[reservationTime]);
            Swal.fire({
              title: t("msg_are_you_sure"),
              text: qr_popup_text,
              icon : "question" ,
              showCancelButton: true,
              confirmButtonColor: "#f8821b",
              cancelButtonColor: "#6c757d",
              confirmButtonText: t("btn_No"),
              cancelButtonText: t("btn_yes"),
              reverseButtons: true,
              customClass: {
                  popup: 'swal-restaurant',
                  confirmButton: 'btn btn-danger col',
                  cancelButton: 'btn btn-secondary col'
              },
              allowOutsideClick: false
          }).then((result) => {
              if (result.isConfirmed && base === ORDER_PLATFORM.WEB) {
                window.location.replace("https://google.com");
              }
              if(result.isConfirmed && base === ORDER_PLATFORM.APP){
                const appData = { action: "go-to", data: "TakeOrderSelectTable" };
                postAppMessage(appData);
              }
          });
          }

          if (response.data.activeOrder) {
            const mySearchParams = new URLSearchParams(window.location.search);
            const alreadyInSession = Boolean(mySearchParams.get("order_id"));
            mySearchParams.set("restaurant_id", response.data.restaurant_id);
            mySearchParams.set("table_id", response.data.tableOrders?.table_id);
            mySearchParams.set("type", "table");
            if (!alreadyInSession && (response.data.activeOrder.order_status === ORDER_STATUS.PENDING || response.data.activeOrder.order_status === ORDER_STATUS.CHECKED_OUT)) {
              mySearchParams.set("order_id", response.data.activeOrder?._id);
              navigate(
                {
                  pathname: "/qr-order/place",
                  search: mySearchParams.toString(),
                },
                {
                  state: {
                    customerDetails: response?.data.tableOrders?.customerDetails,
                  },
                }
              );
            }
          }
          setTimeout(() => {
            setTableDetailsLoader(false);
          }, LOADER_TIMEOUT_TIME);
        })
        .catch((err) => {
          window.dispatchEvent(new CustomEvent("qr-bootstrap-unauthorized", {
            detail: {
              source: "Table details",
              error: err,
            },
          }));
          if (isFirstLoad) {
            setTimeout(() => {
              setTableDetailsLoader(false);
            }, LOADER_TIMEOUT_TIME);
          }
          console.log(err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.pathname, orderRefreshNonce]);

  const changeLanguage = (lang, flag, triggerEvent=true) => {
    i18next.changeLanguage(lang);
    setLocalKey(QR_LANGUAGE_STORAGE_KEY, lang);
    setFlag(flag);
    if(triggerEvent){
      const evt = new CustomEvent("language_change");
      document.dispatchEvent(evt);
      if(base === ORDER_PLATFORM.APP){
        const appData = { action: 'language-changed', data: lang };
        postAppMessage(appData);
      }
    }
  };
  useEffect(() => {
    const handleLanguageChange = () => {
      setLangChanged((current) => !current);
    };
    document.addEventListener("language_change", handleLanguageChange);
    return () => {
      document.removeEventListener("language_change", handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    setShowWebHeder(QRHeaderStatus(base));
  }, [location]);

  // Cart icon → the placed order (/place). Always shown once the table's active
  // order has at least one confirmed item; the badge is the total confirmed
  // item count (base + sub-orders). Shared by both the web and app headers.
  const CartButton = () =>
    confirmedItemCount > 0 ? (
      <button
        className="btn btn-icon position-relative"
        onClick={() => {
          const mySearchParams = new URLSearchParams(window.location.search);
          navigate({
            pathname: "/qr-order/place",
            search: mySearchParams.toString(),
          });
        }}
      >
        <i className="ti ti-shopping-cart"></i>
        <span className="cart-badge">{confirmedItemCount > 99 ? "99+" : confirmedItemCount}</span>
      </button>
    ) : null;

  // Shared right cluster for both headers. Order left→right: table number, cart,
  // language (language sits rightmost). No other labels in either header.
  const LanguageDropdown = () => (
    <div className="dropdown profile-dropdown lang-dropdown ms-3">
      <button className="dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
        <div className="avatar avatar-xs">
          <img src={flag} alt="Japanese-flag" />
        </div>
      </button>
      <ul className="dropdown-menu">
        <li
          onClick={(e) => {
            e.preventDefault();
            changeLanguage("ja", japanese);
          }}
        >
          <button className="dropdown-item">
            <figure className="avatar avatar-xxs">
              <img src={japanese} alt="Japanese-flag" />
            </figure>
            <span>Japanese</span>
          </button>
        </li>
        <li
          onClick={(e) => {
            e.preventDefault();
            changeLanguage("en", english);
          }}
        >
          <button className="dropdown-item">
            <figure className="avatar avatar-xxs">
              <img src={english} alt="UK-flag" />
            </figure>
            <span>English</span>
          </button>
        </li>
      </ul>
    </div>
  );

  const TableNumber = () => (
    <div className="qr-table-number">
      {t("label_table1")} <span>{TrimText(tableData?.table_number, 4)}</span>
    </div>
  );

  // Right cluster: cart then language (language rightmost). Table number is
  // rendered separately and centered, so the cart sits between the (centered)
  // table name and the language switcher.
  const HeaderRight = () => (
    <div className="qr-head-right d-flex align-items-center gap-2">
      {CartButton()}
      {LanguageDropdown()}
    </div>
  );

  const WebHeader = () => {
    const isSuccess = window?.location?.pathname === '/qr-order/success';
    return (
      <div className="qr-header d-flex align-items-center">
        <div className="d-flex align-items-center" style={{ flex: 1 }}>
          {
            // In the waiter app (base=APP) the WebHeader is used on /qr-order/place,
            // which otherwise has no back affordance. Give it a back button that
            // returns to the native table list (same bridge action AppHeader uses).
            base === ORDER_PLATFORM.APP ? (
              <button
                className="btn btn-icon position-relative bg-transparent"
                onClick={() => {
                  const appData = { action: "go-to", data: "TakeOrderSelectTable" };
                  postAppMessage(appData);
                }}
                aria-label="Back"
              >
                <i className="ti ti-arrow-left"></i>
              </button>
            ) : null
          }
        </div>
        {TableNumber()}
        <div className="d-flex align-items-center justify-content-end" style={{ flex: 1 }}>
          {isSuccess ? null : HeaderRight()}
        </div>
      </div>
    )
  };
  const AppHeader = () => {
    const isSuccess = window?.location?.pathname === '/qr-order/success';
    return (
      <div className="qr-header d-flex align-items-center">
        <div className="d-flex align-items-center" style={{ flex: 1 }}>
          <button
            className="btn btn-icon position-relative bg-transparent"
            onClick={() => {
              if(window?.location?.pathname === '/qr-order' || window?.location?.pathname === '/qr-order/success'){
                const appData = { action: "go-to", data: "TakeOrderSelectTable" };
                postAppMessage(appData);
              }else{
                window?.history?.back();
              }
            }}
          >
            <i className="ti ti-arrow-left"></i>
          </button>
        </div>
        {TableNumber()}
        <div className="d-flex align-items-center justify-content-end" style={{ flex: 1 }}>
          {isSuccess ? null : HeaderRight()}
        </div>
      </div>
    )
  };
  return (
    <>
      {!tableDetailsLoader ? (
        <>
          {
            showWebHeder ?
              WebHeader()
              :
              AppHeader()
          }          
        </>
      ) : (
        <div className="qr-header d-flex align-items-center justify-content-between">
          <Skeleton containerClassName="flex-fill me-3" style={{ height: "37px" }} />
          <div className="qr-head-right d-flex align-items-center gap-2">
            <Skeleton style={{ height: "37px", width: "37px" }} />
            <Skeleton style={{ height: "37px", width: "86px" }} />
            <Skeleton style={{ height: "37px", width: "37px" }} className="rounded-circle" />
          </div>
        </div>
      )}
    </>
  );
}

export default Header;
